type IdleOptions = {
  /** requestIdleCallback 超时（ms），不支持时退化为 setTimeout */
  timeoutMs?: number;
  /** 进入 idle / load 回调后再延迟执行（ms），用于多任务错峰 */
  delayMs?: number;
};

/**
 * 在 window.load 之后执行非关键任务（埋点、监控等），避免与首屏 API / LCP 抢带宽。
 * @returns 取消函数（组件卸载时调用）
 */
export function runAfterWindowLoad(callback: () => void, delayMs = 0): () => void {
  let cancelled = false;
  let timerId: ReturnType<typeof window.setTimeout> | null = null;

  const run = () => {
    if (cancelled) {
      return;
    }
    if (delayMs > 0) {
      timerId = window.setTimeout(callback, delayMs);
      return;
    }
    callback();
  };

  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run, { once: true });
  }

  return () => {
    cancelled = true;
    if (timerId !== null) {
      window.clearTimeout(timerId);
    }
  };
}

/**
 * 在浏览器空闲时执行非关键任务；不支持 requestIdleCallback 时退化为短延迟 setTimeout。
 */
export function runWhenIdle(callback: () => void, options: IdleOptions = {}): () => void {
  const { timeoutMs = 3000, delayMs = 0 } = options;
  let cancelled = false;
  let idleId: number | null = null;
  let timerId: ReturnType<typeof window.setTimeout> | null = null;

  const run = () => {
    if (cancelled) {
      return;
    }
    if (delayMs > 0) {
      timerId = window.setTimeout(callback, delayMs);
      return;
    }
    callback();
  };

  if (typeof window.requestIdleCallback === 'function') {
    idleId = window.requestIdleCallback(run, { timeout: timeoutMs });
  } else {
    timerId = window.setTimeout(run, Math.min(timeoutMs, 2000));
  }

  return () => {
    cancelled = true;
    if (idleId !== null && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleId);
    }
    if (timerId !== null) {
      window.clearTimeout(timerId);
    }
  };
}
